import React, { useState, useMemo, useCallback } from "react";
import { Progress } from "reactstrap";

import GradeHeader from "../components/GradeHeader";
import GradeGrid from "../components/GradeGrid";
import ClassPlanModal from "./components/ClassPlanModal";

import NeoLoader from "../../../components/Common/NeoLoader";
import { Breadcrumb, NeoCard, NeoButton, NeoBadge } from "../../../components/Common";
import { usePlanningData } from "./hooks/usePlanningData";
import { getStartOfWeek } from "../../../utils/date";

/**
 * PlanningPage
 * Grade de Planejamento Inteligente de Aulas.
 */
const PlanningPage = () => {
    const [referenceDate, setReferenceDate] = useState(new Date());
    const [view, setView] = useState("week");
    const [turn, setTurn] = useState("all");

    const {
        sessions,
        sessionGroups,
        loading: loadingData,
        generating,
        progress,
        generateAllPlans,
    } = usePlanningData({ referenceDate, view });

    const weekStart = useMemo(() => getStartOfWeek(referenceDate), [referenceDate]);

    const [selectedSession, setSelectedSession] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleSelectSchedule = useCallback((schedule) => {
        setSelectedSession(schedule);
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedSession(null);
    }, []);

    const groupsWithoutPlan = useMemo(() => {
        return sessionGroups.filter(g => !g.hasPlans);
    }, [sessionGroups]);

    const progressPercent = progress.total > 0
        ? Math.round((progress.current / progress.total) * 100)
        : 0;

    if (loadingData) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <NeoLoader type="inline" loading={loadingData} />
            </div>
        );
    }

    return (
        <div className="h-100 w-100 pb-5">
            {/* TOP BAR */}
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between mb-4 gap-3">
                <div>
                    <Breadcrumb title="Planejamento de Aulas" />
                    <p className="text-muted mb-0 font-size-13 mt-0.5">
                        Geração e controle do planejamento de aulas por turma e período.
                    </p>
                </div>

                {/* Ações do painel */}
                <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2 flex-shrink-0">
                    {groupsWithoutPlan.length > 0 && !generating && (
                        <NeoBadge color="warning" icon="mdi-alert-circle-outline">
                            {groupsWithoutPlan.length} turma(s) sem plano
                        </NeoBadge>
                    )}
                    <NeoButton
                        variant="primary"
                        size="sm"
                        disabled={generating || sessionGroups.length === 0}
                        onClick={generateAllPlans}
                        loading={generating}
                        loadingText="Gerando..."
                        icon="mdi-auto-fix"
                        className="neo-filter-search-btn"
                    >
                        Gerar Planejamento
                    </NeoButton>
                </div>
            </div>

            <div className="border-top mb-4"></div>

            {/* Barra de Progresso */}
            {generating && (
                <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="font-size-12 text-muted">
                            <i className="mdi mdi-loading mdi-spin me-1"></i>
                            Atualizando planejamento...
                        </span>
                        <span className="font-size-12 fw-bold text-brand-primary">
                            {progress.current}/{progress.total} turma(s)
                        </span>
                    </div>
                    <Progress
                        value={progressPercent}
                        color="primary"
                        animated
                        striped
                        className="neo-progress-slim"
                    />
                </div>
            )}

            {/* Controles da Grade (turno, visão, data) */}
            <NeoCard className="mb-4" bodyClassName="py-3">
                <GradeHeader
                    turn={turn}
                    onTurnChange={setTurn}
                    view={view}
                    onViewChange={setView}
                    referenceDate={referenceDate}
                    onReferenceDateChange={setReferenceDate}
                />
            </NeoCard>

            {/* Grade */}
            {!loadingData && sessions.length === 0 ? (
                <NeoCard>
                    <div className="text-center py-5">
                        <i className="mdi mdi-clipboard-text-off-outline font-size-48 text-muted d-block mb-3"></i>
                        <h5 className="text-muted">Nenhuma turma avaliativa encontrada</h5>
                        <p className="text-muted font-size-13 mb-0">
                            Cadastre turmas com atividades que possuam o <strong>Sistema de Avaliação</strong> ativado
                            para que apareçam aqui.
                        </p>
                    </div>
                </NeoCard>
            ) : (
                <GradeGrid
                    turn={turn}
                    view={view}
                    referenceDate={referenceDate}
                    weekStart={weekStart}
                    schedules={sessions}
                    loading={loadingData}
                    onSelectSchedule={handleSelectSchedule}
                />
            )}

            {/* Modal de Planejamento */}
            <ClassPlanModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                session={selectedSession}
            />
        </div>
    );
};

export default PlanningPage;
